import { Box, Card, Dialog, Step, StepLabel, Stepper } from '@mui/material';
import axios from 'axios';
import imageCompression from 'browser-image-compression';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import {
    DeviceHardware,
    InstallDeviceRequest,
    Malfunction,
    MalfunctionTypeEnum,
    NewDevice,
} from '../../../../backendsdk';
import useApi from '../../../../hooks/api';
import useIsMobile from '../../../../hooks/isMobile';
import useProfile from '../../../../hooks/profile';
import OpenEyesLogo from './assets/openeyes-logo.svg';
import SetupToolLogo from './assets/setup-tool-logo.svg';
import Completed from './pages/Completed';
import DeviceFeedback from './pages/DeviceFeedback';
import InstallationParams, { EMPTY_PARAMS, Params, convertParam } from './pages/InstallationParams';
import InstallerIdentification from './pages/InstallerIdentification';
import OldDevice from './pages/OldDevice';
import ProcessIntro from './pages/ProcessIntro';
import SelectNewDevice from './pages/SelectNewDevice';
import SelectVehicleForAdjust from './pages/SelectVehicleForAdjust';
import UploadPhoto from './pages/UploadPhoto';
import VerifyCamera from './pages/VerifyCamera';
import { AnalysisResult, fallbackValue } from './utils/analysis';
import { getStoredValue, setStoredValue } from './utils/storage';
import getWorker from './workers/worker';

/** Default camera identifiers when stream_mapping is not provided (e.g. adjust flow or skipped feedback). */
export const getDefaultStreamMapping = (hasRightBsd: boolean, hasLeftBsd: boolean): Record<string, string> => {
    const mapping: Record<string, string> = {
        front_facing: '0',
        in_cabin: hasRightBsd || hasLeftBsd ? '2' : '1',
    };
    if (hasLeftBsd) {
        mapping.bsd_left = '1';
    }
    if (hasRightBsd) {
        mapping.bsd_right = '3';
    }
    return mapping;
};

export const HORIZONTAL_APERTURE: Record<DeviceHardware, number> = {
    [DeviceHardware.MitacK220]: 87,
    [DeviceHardware.MitacSprint]: 87,
    [DeviceHardware.ReachengR1]: 57,
};

export const VERTICAL_APERTURE: Record<DeviceHardware, number> = {
    [DeviceHardware.MitacK220]: 64,
    [DeviceHardware.MitacSprint]: 64,
    [DeviceHardware.ReachengR1]: 43,
};

export const cleanup = (suffix: string) => {
    localStorage.removeItem(`OE-page-${suffix}`);
    localStorage.removeItem(`OE-mode-${suffix}`);
    localStorage.removeItem(`OE-step-${suffix}`);
    localStorage.removeItem(`OE-old-imei-${suffix}`);
    localStorage.removeItem(`OE-new-device-${suffix}`);
    localStorage.removeItem(`OE-params-${suffix}`);
    localStorage.removeItem(`OE-form-stage-${suffix}`);
    localStorage.removeItem(`OE-adjust-device-${suffix}`);
};

export enum Pages {
    InstallerIdentification,
    Intro,
    VerifyCamera,
    OldDevice,
    SelectNewDevice,
    InstallationParams,
    UploadPhoto,
    Feedback,
    Completed,
}

export const PAGE_ORDER: Record<Modes, Pages[]> = {
    install: [Pages.SelectNewDevice, Pages.InstallationParams, Pages.UploadPhoto, Pages.Feedback],
    switch: [Pages.OldDevice, Pages.SelectNewDevice, Pages.InstallationParams, Pages.UploadPhoto, Pages.Feedback],
    remove: [Pages.OldDevice],
    verify: [Pages.VerifyCamera],
    adjust: [Pages.OldDevice, Pages.InstallationParams, Pages.UploadPhoto, Pages.Feedback],
};

export interface WorkerParams {
    instHeight: number;
    instDepth: number;
    instHoodHeight: number;
    'vehicle-width': number;
    horizontalAperture: number;
    verticalAperture: number;
    distance?: number;
}

interface SetupToolProps {
    malfunction?: Malfunction;
    mode: Modes;
    imei?: string;
    hardware?: DeviceHardware;
    policyId?: number;
    expectedLicensePlate?: string;
    dashboardLink: React.ReactNode;
}

export const SETUP_TOOL_MODES = ['install', 'switch', 'remove', 'verify', 'adjust'] as const;
export type Modes = typeof SETUP_TOOL_MODES[number];

const MODES_WITH_INTRO = ['install', 'switch', 'adjust'];

const SetupTool: React.FC<SetupToolProps> = (props: SetupToolProps) => {
    const suffix = props.malfunction !== undefined ? `${props.malfunction.id}-${props.mode}` : `free-${props.mode}`;
    const isNestedMode =
        props.mode === 'adjust' &&
        (props.malfunction === undefined ||
            [MalfunctionTypeEnum.InCabinV1, MalfunctionTypeEnum.FrontFacing].includes(props.malfunction.type));
    const [page, setPage] = useState<number>(getStoredValue<number>('OE-page', suffix, Pages.InstallerIdentification));
    const [installerName, setInstallerName] = useState<string>(getStoredValue<string>('OE-installer-name', '', ''));
    const [newDevice, setNewDevice] = useState<NewDevice | undefined>(
        getStoredValue<NewDevice | undefined>('OE-new-device', suffix, undefined),
    );
    const [params, setParams] = useState<Params>(getStoredValue<Params>('OE-params', suffix, EMPTY_PARAMS));
    const [workerParams, setWorkerParams] = useState<WorkerParams | undefined>(
        getStoredValue<WorkerParams | undefined>('OE-worker-params', suffix, undefined),
    );
    const [horizontalPixel, setHorizontalPixel] = useState<number>();
    const [minDistance, setMinDistance] = useState<number>();
    const [groundPixel, setGroundPixel] = useState<number>();
    const [formStage, setFormStage] = useState<number>(getStoredValue<number>('OE-form-stage', suffix, 0));
    const [photo, setPhoto] = useState<File | null>(null);
    const [adjustDevice, setAdjustDevice] = useState<{ imei: string; hardware: DeviceHardware } | undefined>(
        getStoredValue<{ imei: string; hardware: DeviceHardware } | undefined>('OE-adjust-device', suffix, undefined),
    );
    const isMobile = useIsMobile();
    const { i18n } = useTranslation();
    const isRTL = i18n.languages[0] === 'he';
    const { installApi } = useApi();
    const { profile } = useProfile();
    const history = useHistory();

    const effectiveImei = props.imei ?? adjustDevice?.imei;
    const effectiveHardware = props.hardware ?? adjustDevice?.hardware ?? DeviceHardware.ReachengR1;

    const device = useMemo(() => {
        let device = newDevice;
        if (props.mode === 'adjust' && (effectiveImei || newDevice?.imei)) {
            device = {
                hardware: effectiveHardware,
                imei: effectiveImei ?? newDevice?.imei ?? '',
            };
        }
        return device;
    }, [newDevice, props.mode, effectiveImei, effectiveHardware]);

    const modePages = PAGE_ORDER[props.mode];
    let step;
    if (modePages) {
        if (page === Pages.Completed) {
            step = { current: modePages.length, total: modePages.length };
        } else {
            if (modePages.includes(page)) {
                step = { current: modePages.indexOf(page), total: modePages.length };
            }
        }
    }

    useEffect(() => {
        setStoredValue<string>('OE-installer-name', '', installerName);
    }, [installerName]);

    useEffect(() => {
        if (workerParams) {
            setStoredValue('OE-worker-params', suffix, workerParams);
            const worker = getWorker();
            worker.onmessage = (event) => {
                const res = event.data as AnalysisResult;
                setHorizontalPixel(res.segmentPixel);
                setMinDistance(res.minDistance);
                setGroundPixel(res.groundPixel);
            };
            worker.onerror = () => {
                setHorizontalPixel(fallbackValue);
            };
            worker.postMessage(workerParams);
            return () => {
                worker.terminate();
            };
        }
    }, [workerParams]);

    useEffect(() => {
        if (page !== Pages.Completed) {
            setStoredValue<number>('OE-page', suffix, Math.min(page, Pages.UploadPhoto));
        }
    }, [page]);

    useEffect(() => {
        if (device) {
            if (props.mode !== 'switch') {
                localStorage.removeItem('OE-params');
            }
            setStoredValue<NewDevice>('OE-new-device', suffix, device);
        }
    }, [device]);

    useEffect(() => {
        setStoredValue('OE-adjust-device', suffix, adjustDevice);
    }, [adjustDevice, suffix]);

    useEffect(() => {
        document.dir = isRTL ? 'rtl' : 'ltr';
    }, [isRTL]);

    const uploadImage = async (image: File) => {
        if (!device) {
            return;
        }
        try {
            const post_res = await installApi.installV2UploadPost({
                installationFileRequestV2: { device_id: device.imei, file_name: image.name },
            });
            const compressedFile = await imageCompression(image, { maxSizeMB: 1 });
            const config = {
                headers: {
                    'Content-Type': image.type,
                },
            };
            await axios.put(post_res.data.upload_url, compressedFile, config);
        } catch (error) {
            // pass
        }
    };

    const measurementSystem = profile.customer.settings.country === 'us' ? 'IMPERIAL' : 'METRIC';

    const installDevice = (streamMapping?: Record<string, string>) => {
        if (!device) {
            return Promise.reject();
        }

        const hasStreamMapping = streamMapping && Object.keys(streamMapping).length > 0;
        const effectiveStreamMapping = hasStreamMapping
            ? streamMapping
            : getDefaultStreamMapping(!!params.bsdRightDepth, !!params.bsdLeftDepth);

        const offsetFactor = params.offsetDirection === 'left' ? -1 : 1;
        const formattedParams: InstallDeviceRequest = {
            installer: installerName,
            device_id: device.imei,
            customer_name: profile.customer.name,
            license_plate: params.license_plate,
            instHeight: convertParam(parseFloat(params.instHeight), measurementSystem, 'METRIC'),
            instDepth: convertParam(parseFloat(params.instDepth), measurementSystem, 'METRIC'),
            instHoodHeight: convertParam(parseFloat(params.instHoodHeight), measurementSystem, 'METRIC'),
            instHorizontalOffset: convertParam(
                parseFloat(params.instHorizontalOffset) * offsetFactor,
                measurementSystem,
                'METRIC',
            ),
            vehicle_type: params.vehicleType,
            vehicle_weight: parseFloat(params['vehicle-weight']),
            vehicle_width: convertParam(parseFloat(params['vehicle-width']), measurementSystem, 'METRIC'),
            old_imei: effectiveImei ?? '',
            policy_id: props.policyId,
            malfunction_id: props.malfunction?.id,
            stream_mapping: effectiveStreamMapping,
            vehicleLength: !!params.vehicleLength
                ? convertParam(parseFloat(params.vehicleLength), measurementSystem, 'METRIC')
                : undefined,
            bsdLeftDepth: !!params.bsdLeftDepth
                ? convertParam(parseFloat(params.bsdLeftDepth), measurementSystem, 'METRIC')
                : undefined,
            bsdLeftHeight: !!params.bsdLeftHeight
                ? convertParam(parseFloat(params.bsdLeftHeight), measurementSystem, 'METRIC')
                : undefined,
            bsdRightDepth: !!params.bsdRightDepth
                ? convertParam(parseFloat(params.bsdRightDepth), measurementSystem, 'METRIC')
                : undefined,
            bsdRightHeight: !!params.bsdRightHeight
                ? convertParam(parseFloat(params.bsdRightHeight), measurementSystem, 'METRIC')
                : undefined,
        };

        return installApi.installPost({ installDeviceRequest: formattedParams });
    };

    useEffect(() => {
        if (photo) {
            uploadImage(photo);
        }
    }, [photo]);

    useEffect(() => {
        if (location.pathname.endsWith('/clear')) {
            cleanup(suffix);
            history.replace(props.malfunction !== undefined ? `/tasks/${props.malfunction.id}` : '/tasks');
        }
    }, [location.pathname]);

    const handleNext = () => {
        setPage((prev) => {
            const pages = PAGE_ORDER[props.mode];
            const lastPage = pages[pages.length - 1];
            if (prev === lastPage) {
                return Pages.Completed;
            }
            return pages[pages.indexOf(page) + 1];
        });
    };

    const handleBack = () => {
        const pages = PAGE_ORDER[props.mode];
        const firstPage = pages[0];
        const nextPage =
            page === firstPage
                ? MODES_WITH_INTRO.includes(props.mode)
                    ? Pages.Intro
                    : Pages.InstallerIdentification
                : pages[pages.indexOf(page) - 1];
        if (nextPage === Pages.Intro && props.mode === 'adjust') {
            setAdjustDevice(undefined);
        }
        setPage(nextPage);
    };

    let currentPage;
    if (page === Pages.InstallerIdentification) {
        currentPage = (
            <InstallerIdentification
                installerName={installerName}
                setInstallerName={setInstallerName}
                onNext={() => setPage(MODES_WITH_INTRO.includes(props.mode) ? Pages.Intro : PAGE_ORDER[props.mode][0])}
                dashboardLink={props.dashboardLink}
            />
        );
    } else if (page === Pages.VerifyCamera) {
        currentPage = (
            <VerifyCamera
                malfunctionId={props.malfunction?.id}
                installerName={installerName}
                customerName={profile.customer.name}
                onBack={handleBack}
                imei={effectiveImei ?? device?.imei}
                hardware={effectiveHardware}
                dashboardLink={props.dashboardLink}
            />
        );
    } else if (page === Pages.Intro) {
        currentPage = (
            <ProcessIntro
                mode={props.mode}
                onNext={handleNext}
                onBack={() => {
                    if (isNestedMode) {
                        history.push(
                            props.malfunction !== undefined ? `/tasks/${props.malfunction.id}/verify` : '/tasks/verify',
                        );
                    } else {
                        setPage(Pages.InstallerIdentification);
                    }
                }}
                dashboardLink={props.dashboardLink}
            />
        );
    } else if (page === Pages.OldDevice) {
        if (effectiveImei) {
            currentPage = (
                <OldDevice
                    mode={props.mode}
                    suffix={suffix}
                    malfunctionId={props.malfunction?.id}
                    customerName={profile.customer.name}
                    installerName={installerName}
                    oldImei={effectiveImei}
                    params={params}
                    setParams={setParams}
                    onBack={handleBack}
                    onNext={handleNext}
                    dashboardLink={props.dashboardLink}
                />
            );
        } else {
            currentPage = (
                <SelectVehicleForAdjust
                    onSelect={(imei, hardware) => setAdjustDevice({ imei, hardware })}
                    onBack={handleBack}
                    dashboardLink={props.dashboardLink}
                />
            );
        }
    } else if (page === Pages.SelectNewDevice) {
        currentPage = (
            <SelectNewDevice
                mode={props.mode}
                suffix={suffix}
                setNewDevice={setNewDevice}
                onBack={handleBack}
                onNext={handleNext}
                dashboardLink={props.dashboardLink}
            />
        );
    } else if (page === Pages.InstallationParams && !!device) {
        currentPage = (
            <InstallationParams
                suffix={suffix}
                customer={profile.customer}
                mode={props.mode}
                newDevice={device}
                params={params}
                formStage={formStage}
                setFormStage={setFormStage}
                setParams={setParams}
                onBack={handleBack}
                onNext={handleNext}
                runWorker={(params: {
                    instHeight: string;
                    instDepth: string;
                    instHoodHeight: string;
                    'vehicle-width': string;
                }) => {
                    const hardware = device.hardware;
                    setWorkerParams({
                        instHeight: convertParam(parseFloat(params.instHeight), measurementSystem, 'METRIC'),
                        instDepth: convertParam(parseFloat(params.instDepth), measurementSystem, 'METRIC'),
                        instHoodHeight: convertParam(parseFloat(params.instHoodHeight), measurementSystem, 'METRIC'),
                        'vehicle-width': convertParam(parseFloat(params['vehicle-width']), measurementSystem, 'METRIC'),
                        horizontalAperture: HORIZONTAL_APERTURE[hardware || DeviceHardware.ReachengR1],
                        verticalAperture: VERTICAL_APERTURE[hardware || DeviceHardware.ReachengR1],
                    });
                }}
                dashboardLink={props.dashboardLink}
                expectedLicensePlate={props.expectedLicensePlate}
            />
        );
    } else if (page === Pages.UploadPhoto) {
        currentPage = (
            <UploadPhoto
                photo={photo}
                setPhoto={setPhoto}
                onBack={handleBack}
                onNext={handleNext}
                dashboardLink={props.dashboardLink}
            />
        );
    } else if (page === Pages.Feedback && device) {
        currentPage = (
            <DeviceFeedback
                horizontalPixel={horizontalPixel}
                groundPixel={groundPixel}
                minDistance={minDistance}
                newDevice={device}
                customer={profile.customer}
                suffix={suffix}
                setWorkerParams={setWorkerParams}
                onInstall={installDevice}
                onBack={handleBack}
                onNext={handleNext}
                dashboardLink={props.dashboardLink}
                vehicleLength={params.vehicleLength}
                bsdLeftHeight={params.bsdLeftHeight}
                bsdRightHeight={params.bsdRightHeight}
            />
        );
    } else if (page === Pages.Completed) {
        currentPage = (
            <Completed
                mode={props.mode}
                suffix={suffix}
                onDone={() => {
                    if (isNestedMode && props.malfunction !== undefined) {
                        history.replace(`/tasks/${props.malfunction.id}/adjust`);
                    } else {
                        history.replace('/tasks');
                    }
                }}
            />
        );
    }

    const content = (
        <Box
            sx={{
                width: '85%',
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                position: 'relative',
                minHeight: 0,
                flexShrink: 0,
            }}
        >
            {step !== undefined && step.total > 1 ? (
                <Stepper
                    id="stepper"
                    data-current-step={step.current}
                    activeStep={step.current}
                    alternativeLabel
                    sx={{ position: 'absolute', top: '10px', width: '100%', flexShrink: 0 }}
                >
                    {Array.from({ length: step.total }, (_, index) => (
                        <Step key={index}>
                            <StepLabel></StepLabel>
                        </Step>
                    ))}
                </Stepper>
            ) : null}
            <Box
                sx={{
                    width: '60px',
                    mt: ['install', 'switch', 'adjust'].includes(props.mode) ? '60px' : '20px',
                    mb: 0.5,
                    flexShrink: 0,
                }}
            >
                <img src={SetupToolLogo} alt="Setup Tool Logo" width="100%" />
            </Box>
            <Box sx={{ width: '200px', flexShrink: 0 }}>
                <img src={OpenEyesLogo} alt="OpenEyes Logo" width="100%" />
            </Box>
            {currentPage}
        </Box>
    );

    const mobileContainer = (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                overflow: 'auto',
                minHeight: 0,
            }}
        >
            {content}
        </Box>
    );

    const desktopContainer = (
        <Card
            sx={{
                width: '100%',
                maxWidth: '400px',
                height: '100%',
                maxHeight: '800px',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                overflow: 'auto',
            }}
        >
            {content}
        </Card>
    );

    return (
        <Dialog id="setup-tool-dialog" open={true} fullScreen>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: '100%',
                    height: '100%',
                }}
            >
                {isMobile ? mobileContainer : desktopContainer}
            </Box>
        </Dialog>
    );
};

export default SetupTool;
